import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import DrawerLayout from './components/Layout/DrawerLayout';
import Dashboard from './pages/Dashboard';
import ContentGeneration from './pages/ContentGeneration';
import SchedulePosts from './pages/SchedulePosts';
import PlatformIntegrations from './pages/PlatformIntegrations';
import Analytics from './pages/Analytics';
import SetupWizard from './SetupWizard/SetupWizard';
import Register from './components/Register';
import Login from './components/Login';
import UserProfile from './pages/UserProfile';
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import SubscriptionUpgrade from './pages/SubscriptionUpgrade';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import './styles/App.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import AdminPanel from './pages/AdminPanel';
import { NotificationProvider } from './contexts/NotificationContext';
import { Notifications } from './components/Notifications';
import { HelmetProvider } from 'react-helmet-async';
import { trackPageView, trackAuthEvent, trackSetupProgress } from './utils/analytics';

// Create a wrapper component for tracking page views
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);

  return null;
}

function App() {
  const [setupCompleted, setSetupCompleted] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for authentication token on load
    const token = localStorage.getItem('token');
    const authState = localStorage.getItem('isAuthenticated') === 'true';
    const adminState = localStorage.getItem('isAdmin') === 'true';

    if (token && authState) {
      setIsAuthenticated(true);
      setIsAdmin(adminState);
    }

    // Check if setup wizard has been completed
    const completed = localStorage.getItem('setupCompleted');
    setSetupCompleted(completed === 'true');

    setLoading(false);
  }, []);

  const completeSetup = () => {
    localStorage.setItem('setupCompleted', 'true');
    setSetupCompleted(true);
    trackSetupProgress('complete', true);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('setupCompleted');
    localStorage.removeItem('isAdmin');
    setIsAuthenticated(false);
    setSetupCompleted(false);
    setIsAdmin(false);
    trackAuthEvent('logout', 'user_initiated');
  };

  const ProtectedRoute = () => {
    if (loading) return null;
    if (!isAuthenticated) {
      return <Navigate to="/" />;
    }
    if (isAdmin) {
      return <DrawerLayout onLogout={logout}><Outlet /></DrawerLayout>;
    }
    if (!setupCompleted) {
      return <Navigate to="/setupwizard" />;
    }
    return <DrawerLayout onLogout={logout}><Outlet /></DrawerLayout>;
  };

  return (
    <HelmetProvider>
      <Provider store={store}>
        <PersistGate loading={<div>Loading...</div>} persistor={persistor}>
          <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <NotificationProvider>
              <Router>
                <PageViewTracker />
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/login" element={
                    <Login onLogin={(userData) => {
                      setIsAuthenticated(true);
                      setIsAdmin(userData.is_admin);
                      localStorage.setItem('isAuthenticated', 'true');
                      localStorage.setItem('isAdmin', userData.is_admin);
                      trackAuthEvent('login', userData.login_method || 'email');
                    }} />
                  } />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/setupwizard" element={
                    isAuthenticated && !setupCompleted && !isAdmin ? 
                    <SetupWizard onComplete={completeSetup} /> : 
                    <Navigate to={isAdmin ? "/admin" : "/dashboard"} />
                  } />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route element={<ProtectedRoute />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/content-generation" element={<ContentGeneration />} />
                    <Route path="/schedule-posts" element={<SchedulePosts />} />
                    <Route path="/platform-integrations" element={<PlatformIntegrations />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/profile" element={<UserProfile />} />
                    <Route path="/admin" element={<AdminPanel />} />
                    <Route path="/subscription/upgrade" element={<SubscriptionUpgrade />} />
                    <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                  </Route>
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </Router>
              <Notifications />
            </NotificationProvider>
          </GoogleOAuthProvider>
        </PersistGate>
      </Provider>
    </HelmetProvider>
  );
}

export default App;
